'use client';

import {useRouter} from 'next/navigation';
import {useTranslation} from 'react-i18next';
import {usePagination} from '../../hooks';
import {CardStatusChip, statusBadgeIcon, type ListingCardProps} from '../listing';
import {ListingPage, type ListingPageEmptyState} from '../listing/ListingPage';
import {openRegisterInterest} from '../register-interest/register-interest-controller';
import {statusBadgeKey, type CardListingItem, type CardListingTrackedItem} from './listing-card-item';

export interface EntityGridListingAnalytics {
  onCardClick: (item: CardListingTrackedItem) => void;
  onViewItemClick: (item: CardListingTrackedItem) => void;
  onRegisterInterestClick: (item?: CardListingTrackedItem) => void;
}

export interface EntityGridListingProps {
  titleKey: string;
  eyebrow?: string;
  items: CardListingItem[];
  empty: ListingPageEmptyState;
  viewLabelKey?: string;
  analytics: EntityGridListingAnalytics;
}

export function EntityGridListing({titleKey, eyebrow, items, empty, viewLabelKey = 'view_details', analytics}: EntityGridListingProps) {
  const {t} = useTranslation();
  const router = useRouter();
  const {pageItems, paginationProps} = usePagination(items);

  const cards: ListingCardProps[] = pageItems.map(item => {
    const tracked = item.id ? {id: item.id, name: item.title} : undefined;
    const badgeKeys = statusBadgeKey(item);
    // Status renders either as the image corner tag (with icon, `statusAsTag`) or, by default, as a body badge.
    const statusTagKeys = item.statusAsTag ? badgeKeys : undefined;
    const statusBadgeKeys = !item.statusAsTag ? badgeKeys : undefined;
    const statusBadge = statusBadgeKeys?.map(key => ({label: t(key), icon: statusBadgeIcon(key)}));
    const statusTags = statusTagKeys?.map(key => <CardStatusChip key={key} statusKey={key} label={t(key)} />);
    return {
      id: item.id,
      title: item.title,
      image: item.image,
      href: item.href,
      primaryMeta: item.primaryMeta,
      meta: item.meta,
      badges: item.badges ?? statusBadge,
      tags: statusTags?.length ? statusTags : item.tags,
      onClick: tracked
        ? () => {
            analytics.onCardClick(tracked);
          }
        : undefined,
      hoverActions: [
        {
          label: t('register_interest'),
          onClick: () => {
            analytics.onRegisterInterestClick(tracked);
            openRegisterInterest(item.registerInterest);
          },
        },
        {
          label: t(viewLabelKey),
          onClick: () => {
            if (tracked) {
              analytics.onViewItemClick(tracked);
            }
            router.push(item.href);
          },
        },
      ],
    };
  });

  return <ListingPage title={t(titleKey)} eyebrow={eyebrow} cards={cards} empty={empty} pagination={paginationProps} />;
}

EntityGridListing.displayName = 'EntityGridListing';
